import React, { useState, useEffect, useCallback } from "react";
import { PageFormLayout, FormSection, FormActions, FieldLabel } from "./ui";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../services";
import { useTranslation } from "react-i18next";
import NativeInput from "./NativeInput";
import { invalidateSetupGuideCache } from "../hooks/useSetupGuideStatus";

const DAYS_OF_WEEK = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function isValidTime(time) {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
}

function isStartBeforeEnd(start, end) {
  if (!isValidTime(start) || !isValidTime(end)) return false;
  return start < end;
}

function AddNewBranch() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  // State declarations
  const [branchName, setBranchName] = useState("");
  const [branchNameAr, setBranchNameAr] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [availability] = useState([]);
  const [breakHours] = useState([]);
  const [holidayHours] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { storeId } = location.state || {};
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  // Keeta shop selection on add-branch — HIDDEN FOR NOW
  // const [keetaShops, setKeetaShops] = useState([]);
  // const [selectedKeetaShopId, setSelectedKeetaShopId] = useState("");
  // const [keetaAvailable, setKeetaAvailable] = useState(false);
  const [foodicsOutlets, setFoodicsOutlets] = useState([]);
  const [selectedFoodicsOutletId, setSelectedFoodicsOutletId] = useState("");
  const [foodicsAvailable, setFoodicsAvailable] = useState(false);
  const [branches, setBranches] = useState([]);
  const [apps, setApps] = useState([]);
  // Field-specific error states
  const [fieldErrors, setFieldErrors] = useState({
    branchName: "",
    branchNameAr: "",
    email: "",
    phone: "",
    city: "",
    address: "",
    latitude: "",
    longitude: "",
    // keetaShopId: "",
    foodicsOutletId: "",
  });

  // Google Maps integration - HIDDEN FOR NOW
  // const [showMap, setShowMap] = useState(false);
  // const [mapCenter, setMapCenter] = useState({ lat: 24.7136, lng: 46.6753 }); // Default to Riyadh

  // useEffect and handler functions remain the same
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const token = localStorage.getItem("token");
        setLoadingCities(true);
        const response = await api.get("/cities", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCities(response.data.data);
        setLoadingCities(false);
      } catch (err) {
        console.error("Error fetching cities:", err);
        setError(`${t("Failed to load cities. Please try again later.")}`);
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, [t]);

  const fetchStoreDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error(
        "Error fetching store details:",
        error.response?.data || error.message
      );
    }
  }, [storeId]);

  useEffect(() => {
    fetchStoreDetails();
  }, [fetchStoreDetails]);

  // Keeta shop selection on add-branch — HIDDEN FOR NOW
  // const fetchAllKeetaShops = useCallback(async () => {
  //   try {
  //     const response = await api.get(`/keeta-shops/store/${storeId}/filtered`, {
  //       headers: {
  //         Authorization: `Bearer ${localStorage.getItem("token")}`,
  //       },
  //     });
  //     const shops = response.data.data?.shops;
  //     setKeetaShops(shops);
  //   } catch (error) {
  //     console.error("Error:", error);
  //     setKeetaShops([]);
  //   }
  // }, [storeId]);

  const fetchAllFoodicsOutlets = useCallback(async () => {
    try {
      const response = await api.get(
        `/foodics-shops/store/${storeId}/filtered`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("Foodics API Response:", response.data);

      const outlets = response.data.data?.shops || [];
      setFoodicsOutlets(outlets);
    } catch (error) {
      console.error("Error fetching Foodics outlets:", error);
      setFoodicsOutlets([]);
    }
  }, [storeId]);
  useEffect(() => {
    const fetchStoreApps = async () => {
      if (!storeId) return;

      try {
        const token = localStorage.getItem("token");

        const response = await api.get(`/stores/${storeId}/apps`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const apps = response.data?.apps || [];

        // Keeta shop selection on add-branch — HIDDEN FOR NOW
        // const keetaExists = apps.some(
        //   (app) => app.application?.toLowerCase() === "keeta"
        // );
        // setKeetaAvailable(keetaExists);

        const foodicsExists = apps.some(
          (app) => app.application?.toLowerCase() === "foodics"
        );

        setFoodicsAvailable(foodicsExists);
      } catch (err) {
        console.error("Error fetching store apps:", err);
      }
    };

    fetchStoreApps();
  }, [storeId]);

  const fetchApps = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/apps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApps(response.data.apps || []);
    } catch (err) {
      console.error("Error fetching apps:", err.message);
    }
  }, []);

  useEffect(() => {
    // fetchAllKeetaShops(); // Keeta shop selection — HIDDEN FOR NOW
    fetchAllFoodicsOutlets();
    fetchApps();
  }, [fetchAllFoodicsOutlets, fetchApps]);

  // Load Google Maps script - HIDDEN FOR NOW
  // useEffect(() => {
  //     const loadGoogleMapsScript = () => {
  //         if (window.google && window.google.maps) {
  //             return;
  //         }
  //
  //         const script = document.createElement('script');
  //         script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
  //         script.async = true;
  //         script.defer = true;
  //         document.head.appendChild(script);
  //     };

  //     if (city) {
  //         loadGoogleMapsScript();
  //     }
  // }, [city]);

  const validateField = (fieldName, value) => {
    let errorMessage = "";

    switch (fieldName) {
      case "branchName":
        if (!value.trim()) {
          errorMessage = `${t("Branch name is required")}`;
        } else if (value.trim().length > 23) {
          errorMessage = `${t("Branch name must not exceed 23 characters")}`;
        }
        break;
      case "branchNameAr":
        if (value && value.trim()) {
          const arabicRegex = /^[\u0600-\u06FF0-9 ]+$/;
          if (!arabicRegex.test(value.trim())) {
            errorMessage = `${t(
              "Only Arabic letters, numbers, and spaces are allowed"
            )}`;
          }
        }
        break;
      case "email":
        if (!value.trim()) {
          errorMessage = `${t("Email is required")}`;
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errorMessage = `${t("Please enter a valid email address")}`;
        }
        break;
      case "phone":
        if (!value.trim()) {
          errorMessage = `${t("Phone number is required")}`;
        } else {
          // Remove all non-digit characters for validation
          const cleanPhone = value.replace(/\D/g, "");

          // KSA phone number validation
          // Format 1: +966XXXXXXXXX (13 digits starting with 966)
          // Format 2: 966XXXXXXXXX (12 digits starting with 966)
          // Format 3: 05XXXXXXXX (10 digits starting with 05)
          // Format 4: 5XXXXXXXX (9 digits starting with 5)

          if (cleanPhone.length === 13 && cleanPhone.startsWith("966")) {
            // Valid +966 format
          } else if (cleanPhone.length === 12 && cleanPhone.startsWith("966")) {
            // Valid 966 format
          } else if (cleanPhone.length === 10 && cleanPhone.startsWith("05")) {
            // Valid 05 format
          } else if (cleanPhone.length === 9 && cleanPhone.startsWith("5")) {
            // Valid 5 format
          } else {
            errorMessage = `${t(
              "Please enter a valid KSA phone number (e.g., +966501234567, 966501234567, 0501234567, or 501234567)"
            )}`;
          }
        }
        break;
      case "city":
        if (!value.trim()) {
          errorMessage = `${t("City is required")}`;
        }
        break;
      case "address":
        if (!value.trim()) {
          errorMessage = `${t("Address is required")}`;
        }
        break;
      case "latitude":
        if (!value.trim()) {
          errorMessage = `${t("Latitude is required")}`;
        } else if (isNaN(value) || value < -90 || value > 90) {
          errorMessage = `${t("Please enter a valid latitude (-90 to 90)")}`;
        }
        break;
      case "longitude":
        if (!value.trim()) {
          errorMessage = `${t("Longitude is required")}`;
        } else if (isNaN(value) || value < -180 || value > 180) {
          errorMessage = `${t("Please enter a valid longitude (-180 to 180)")}`;
        }
        break;
      default:
        break;
    }

    return errorMessage;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);
    const errorMessage = validateField(fieldName, value);
    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: errorMessage,
    }));
    setError(""); // Clear general error when user starts typing
  };

  const handleCityChange = (selectedCity) => {
    setCity(selectedCity);
    setFieldErrors((prev) => ({
      ...prev,
      city: validateField("city", selectedCity),
    }));

    if (selectedCity) {
      // You can set default coordinates for the selected city here
      // For now, we'll use Riyadh coordinates
      // setMapCenter({ lat: 24.7136, lng: 46.6753 });
    } else {
      // setShowMap(false);
    }
  };

  // Map click handler - HIDDEN FOR NOW
  // const handleMapClick = (event) => {
  //     const lat = event.latLng.lat();
  //     const lng = event.latLng.lng();
  //     setLatitude(lat.toFixed(6));
  //     setLongitude(lng.toFixed(6));
  //
  //     // Clear errors for these fields
  //     setFieldErrors(prev => ({
  //         ...prev,
  //         latitude: '',
  //         longitude: ''
  //     }));
  // };

  const validateAllFields = () => {
    const fields = {
      branchName,
      email,
      phone,
      city,
      address,
      latitude,
      longitude,
    };

    const newFieldErrors = {};
    let hasErrors = false;

    Object.keys(fields).forEach((fieldName) => {
      const errorMessage = validateField(fieldName, fields[fieldName]);
      newFieldErrors[fieldName] = errorMessage;
      if (errorMessage) {
        hasErrors = true;
      }
    });

    setFieldErrors(newFieldErrors);
    return !hasErrors;
  };

  const handleSave = async () => {
    if (!validateAllFields()) {
      setError(`${t("Please fix the errors above before saving.")}`);
      return;
    }

    const validatedAvailability = availability.filter(
      (a) => a.day && a.start && a.end
    );

    if (validatedAvailability.length !== availability.length) {
      setError(
        `${t(
          "All availability entries must have a valid day, start time, and end time."
        )}`
      );
      return;
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem("token");

      const formattedAvailability = validatedAvailability.map(
        ({ day, start, end }) => ({
          day: day.toLowerCase(),
          start,
          end,
        })
      );

      const integrationId = `BRN-${storeId}-${Date.now()}`;

      const payload = {
        name: { en: branchName, ...(branchNameAr ? { ar: branchNameAr } : {}) },
        email,
        address,
        availability: formattedAvailability,
        breakHours,
        holidayHours,
        latitude,
        longitude,
        integrationId,
        phone,
        city,
        // Keeta shop selection — HIDDEN FOR NOW
        // ...(selectedKeetaShopId && { keetaShopId: selectedKeetaShopId }),
        ...(selectedFoodicsOutletId && {
          foodicsBranchId: selectedFoodicsOutletId,
        }),
      };

      await api.post(`/stores/${storeId}/branches`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccessMessage(`${t("Branch added successfully.")}`);
      invalidateSetupGuideCache();
      setTimeout(
        () => navigate(`/manage-branches-v2`, { state: { storeId } }),
        2000
      );
    } catch (err) {
      console.error("Error adding branch:", err.response?.data || err.message);

      // Handle API validation errors
      if (
        err.response?.data?.errors &&
        typeof err.response.data.errors === "object"
      ) {
        // Clear any existing field errors
        setFieldErrors({
          branchName: "",
          email: "",
          phone: "",
          city: "",
          address: "",
          latitude: "",
          longitude: "",
          // keetaShopId: "",
          foodicsOutletId: "",
        });

        // Map API error messages to field errors
        const apiErrors = err.response.data.errors;
        const newFieldErrors = { ...fieldErrors };

        // Map backend field names to frontend field names
        if (apiErrors.name) {
          newFieldErrors.branchName = apiErrors.name;
        }
        if (apiErrors.email) {
          newFieldErrors.email = apiErrors.email;
        }
        if (apiErrors.phone) {
          newFieldErrors.phone = apiErrors.phone;
        }
        if (apiErrors.city) {
          newFieldErrors.city = apiErrors.city;
        }
        if (apiErrors.address) {
          newFieldErrors.address = apiErrors.address;
        }
        if (apiErrors.latitude) {
          newFieldErrors.latitude = apiErrors.latitude;
        }
        if (apiErrors.longitude) {
          newFieldErrors.longitude = apiErrors.longitude;
        }
        // if (apiErrors.keetaShopId) {
        //   newFieldErrors.keetaShopId = apiErrors.keetaShopId;
        // }
        if (apiErrors.foodicsBranchId) {
          newFieldErrors.foodicsOutletId = apiErrors.foodicsBranchId;
        }

        setFieldErrors(newFieldErrors);
        setError(`${t("Please fix the validation errors above.")}`);
      } else {
        // Handle general errors
        setError(
          err.response?.data?.message ||
            `${t("Failed to add branch. Please try again.")}`
        );
      }
      setSuccessMessage("");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/manage-branches-v2", { state: { storeId, storeName: null } });
  };

  return (
    <PageFormLayout rootClass="add-branch" title={t("addnewbranch")}>
      <FormSection title={t("basicinformation")}>
        <div className="oh-form-grid">
          <NativeInput
            label={t("branchnameenglish")}
            required
            tooltip={t("formTooltips.branchNameEn")}
            type="text"
            value={branchName}
            onChange={(e) =>
              handleFieldChange("branchName", e.target.value, setBranchName)
            }
            invalid={!!fieldErrors.branchName}
            feedback={fieldErrors.branchName}
          />
          <NativeInput
            label={t("branchnamearabic")}
            tooltip={t("formTooltips.branchNameAr")}
            type="text"
            value={branchNameAr}
            onChange={(e) =>
              handleFieldChange(
                "branchNameAr",
                e.target.value,
                setBranchNameAr
              )
            }
            invalid={!!fieldErrors.branchNameAr}
            feedback={fieldErrors.branchNameAr}
          />
          <NativeInput
            label={t("email")}
            required
            tooltip={t("formTooltips.branchEmail")}
            type="email"
            value={email}
            onChange={(e) =>
              handleFieldChange("email", e.target.value, setEmail)
            }
            invalid={!!fieldErrors.email}
            feedback={fieldErrors.email}
          />
          <div className="oh-field">
            <FieldLabel required tooltip={t("formTooltips.branchCity")}>
              {t("selectacity")}
            </FieldLabel>
            <select
              className={`form-select${fieldErrors.city ? " is-invalid" : ""}`}
              value={city}
              onChange={(e) => handleCityChange(e.target.value)}
              disabled={loadingCities}
            >
              <option value="">{t("selectacity")}</option>
              {cities.map((cityItem) => (
                <option key={cityItem._id} value={cityItem.name}>
                  {cityItem.name} ({cityItem.name_localized})
                </option>
              ))}
            </select>
            <div className="oh-field__error invalid-feedback">
              {fieldErrors.city || ""}
            </div>
            {loadingCities && (
              <small className="text-muted">Loading cities...</small>
            )}
          </div>
          <NativeInput
            label={t("phone")}
            required
            tooltip={t("formTooltips.branchPhone")}
            type="text"
            value={phone}
            onChange={(e) =>
              handleFieldChange("phone", e.target.value, setPhone)
            }
            invalid={!!fieldErrors.phone}
            feedback={fieldErrors.phone}
          />
        </div>
      </FormSection>

      {foodicsAvailable && (
        <FormSection title={t("Integration Settings")} showDivider>
          <div className="oh-form-grid">
            {/* Keeta shop selection on add-branch — HIDDEN FOR NOW
            {keetaAvailable && (
              <div className="oh-field">
                <FieldLabel tooltip={t("formTooltips.keetaShop")}>
                  {t("Select Keeta Shop")}
                </FieldLabel>
                <select
                  className={`form-select${
                    fieldErrors.keetaShopId ? " is-invalid" : ""
                  }`}
                  value={selectedKeetaShopId}
                  onChange={(e) => setSelectedKeetaShopId(e.target.value)}
                >
                  <option value="">{t("Select a Keeta Shop")}</option>
                  {keetaShops.length > 0 ? (
                    keetaShops.map((shop) => (
                      <option key={shop._id} value={shop._id}>
                        {shop.keetaShopId} - {shop.shopName}
                      </option>
                    ))
                  ) : (
                    <option value="">{t("No Keeta shops available")}</option>
                  )}
                </select>
                <div className="oh-field__error invalid-feedback">
                  {fieldErrors.keetaShopId || ""}
                </div>
              </div>
            )}
            */}
            <div className="oh-field">
                <FieldLabel tooltip={t("formTooltips.foodicsShop")}>
                  {t("Select Foodics Shop")}
                </FieldLabel>
                <select
                  className={`form-select${
                    fieldErrors.foodicsOutletId ? " is-invalid" : ""
                  }`}
                  value={selectedFoodicsOutletId}
                  onChange={(e) =>
                    setSelectedFoodicsOutletId(e.target.value)
                  }
                >
                  <option value="">{t("Select a Foodics Shop")}</option>
                  {foodicsOutlets.length > 0 ? (
                    foodicsOutlets.map((outlet) => (
                      <option key={outlet._id} value={outlet._id}>
                        {`${outlet.foodicsBranchId} - ${
                          outlet.fullName || outlet.branchName
                        }`}
                      </option>
                    ))
                  ) : (
                    <option value="">
                      {t("No Foodics shops available")}
                    </option>
                  )}
                </select>
                <div className="oh-field__error invalid-feedback">
                  {fieldErrors.foodicsOutletId || ""}
                </div>
              </div>
          </div>
        </FormSection>
      )}

      <FormSection title={t("locationinformation")} showDivider>
        <div className="oh-form-grid">
          <div className="oh-form-grid__span-2">
            <NativeInput
              label={t("address")}
              required
              tooltip={t("formTooltips.branchAddress")}
              type="text"
              value={address}
              onChange={(e) =>
                handleFieldChange("address", e.target.value, setAddress)
              }
              invalid={!!fieldErrors.address}
              feedback={fieldErrors.address}
            />
          </div>
          <NativeInput
            label={t("latitude")}
            required
            tooltip={t("formTooltips.branchLatitude")}
            type="text"
            value={latitude}
            onChange={(e) =>
              handleFieldChange("latitude", e.target.value, setLatitude)
            }
            invalid={!!fieldErrors.latitude}
            feedback={fieldErrors.latitude}
          />
          <NativeInput
            label={t("longitude")}
            required
            tooltip={t("formTooltips.branchLongitude")}
            type="text"
            value={longitude}
            onChange={(e) =>
              handleFieldChange("longitude", e.target.value, setLongitude)
            }
            invalid={!!fieldErrors.longitude}
            feedback={fieldErrors.longitude}
          />
        </div>
      </FormSection>

      {error && (
        <div className="alert alert-danger oh-form-alert mt-3" role="alert">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success oh-form-alert mt-3" role="alert">
          {successMessage}
        </div>
      )}

      <FormActions
        isRtl={isArabic}
        cancelLabel={t("cancel")}
        saveLabel={t("save")}
        savingLabel={t("saving")}
        onCancel={handleCancel}
        onSave={handleSave}
        saving={isSaving}
      />
    </PageFormLayout>
  );
}

// Google Maps Component - HIDDEN FOR NOW
// const GoogleMap = ({ center, onMapClick }) => {
//     const mapRef = React.useRef(null);
//     const [map, setMap] = React.useState(null);
//     const [marker, setMarker] = React.useState(null);

//     React.useEffect(() => {
//         if (window.google && window.google.maps && mapRef.current && !map) {
//             const newMap = new window.google.maps.Map(mapRef.current, {
//                 center: center,
//                 zoom: 13,
//                 mapTypeId: window.google.maps.MapTypeId.ROADMAP
//             });

//             const newMarker = new window.google.maps.Marker({
//                 position: center,
//                 map: newMap,
//                 draggable: true
//             });

//             newMap.addListener('click', (event) => {
//                 onMapClick(event);
//                 newMarker.setPosition(event.latLng);
//             });

//             newMarker.addListener('dragend', (event) => {
//                 onMapClick(event);
//             });

//             setMap(newMap);
//             setMarker(newMarker);
//         }
//     }, [center, onMapClick, map]);

//     return <div ref={mapRef} style={{ height: '100%', width: '100%' }} />;
// };

export default AddNewBranch;
